import { useEffect, useRef, useState } from 'react';
import type { ReactNode, RefObject } from 'react';
import { CommandEmpty, CommandList } from '@voll-intelligence/ui-kit/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@voll-intelligence/ui-kit/ui/popover';
import {
  PLACE_LIST_SCROLLBAR_CLASS,
  PlaceCommand,
  PlaceResults,
  type PlacePanelCopy,
  type PlaceSearchState,
  type PlaceSuggestion,
} from './place_panel.js';
import { useRemoteSearch } from '../use_remote_search.js';
import { wantedAttr } from '../field_trigger.js';
import { returnFocusUnlessMoved } from '../return_focus.js';
import { PlaceSheet } from './place_sheet.js';
import {
  TriggerContent,
  TriggerShell,
  type PlaceFieldValue,
  type PlaceFieldVariant,
} from './place_field_trigger.js';

export { PlaceSearchInput } from './place_field_trigger.js';
export type { PlaceFieldValue } from './place_field_trigger.js';

/**
 * O CAMPO DE LUGAR do DS — a pílula `EscolherPraca` do `flight_planner_v2` do
 * desenho, com a lista compartilhada (`PlaceResults`) no popover. É a outra
 * metade da padronização do campo de endereço: o `PlacePanel` unificou a LISTA
 * (hotel e aéreo), este componente unifica o GATILHO das praças do aéreo.
 *
 * ## Busca no campo (decisão do designer, 2026-08-19)
 *
 * Quem digita NÃO digita dentro do popover: aberto, o gatilho vira o input e o
 * valor atual vira a dica (digitar substitui a praça). As setas e o Enter são da
 * lista — o input as ENCAMINHA pro root do cmdk via `dispatchEvent` (o cmdk
 * escuta keydown no próprio root, com ou sem `CommandInput`), que é o que deixa
 * o `Command` morar no popover enquanto o texto entra pelo campo.
 *
 * ## Abertura controlada
 *
 * O par `open`/`onOpenChange` existe pro auto-avanço (fechou um campo, o
 * seguinte abre sozinho — foco não abre popover no Base UI). Ausente, o campo
 * se governa; `defaultOpen` cobre o degrau menor — nascer aberto uma vez (a
 * continuação de um gesto do host), sem o host virar dono da abertura.
 */

/** Clique no gatilho com o popover ABERTO é posicionamento de cursor no input,
 *  não pedido de fechar. */
function closedByTriggerPress(details: { reason?: string } | undefined): boolean {
  return details?.reason === 'trigger-press';
}

/** A dança da abertura — local por padrão, controlada quando o host passa o
 *  par, e o auto-avanço por cima (ver a prop `defaultOpen` do `PlaceField`).
 *  `onOpen` roda a cada abertura: é o reset do termo — reabrir com a busca
 *  anterior mostraria a lista filtrada por algo que a pessoa não digitou. */
function usePlaceFieldOpen(p: {
  openProp: boolean | undefined;
  onOpenChange: ((open: boolean) => void) | undefined;
  defaultOpen: boolean;
  /** Ver `PlaceField.openedByGesture`. */
  hostGesture: boolean;
  disabled: boolean;
  onOpen: () => void;
}) {
  const [openLocal, setOpenLocal] = useState(p.defaultOpen);
  const open = p.openProp ?? openLocal;
  /* Se a abertura corrente veio de um GESTO (clique/teclado no gatilho, ou o
     `defaultOpen` do host, que é continuação de gesto) — é o que decide se o
     input nasce focado. Um `openProp` que vira verdadeiro sem passar por aqui
     (o host abriu sozinho) deixa o cursor onde estava. */
  const [openedByGesture, setOpenedByGesture] = useState(p.defaultOpen && open);
  /* `false` e não `p.defaultOpen`: nascer com `defaultOpen` também é uma
     abertura, e o `onOpen()` do primeiro effect é o que zera o rascunho no host
     controlado — sem ele o campo abriria vazio sobre a lista filtrada pela
     query de antes (achado do CodeRabbit no PR #2464). */
  const prevAuto = useRef(false);
  /* Os effects que chamam `onOpen` são idempotentes na troca de identidade dele: os
     dois só disparam numa BORDA (`prevAuto`/`wasOpen`), que a re-execução não refaz. */
  const { onOpen } = p;
  useEffect(() => {
    if (p.defaultOpen && !prevAuto.current && p.openProp === undefined) {
      setOpenLocal(true);
      setOpenedByGesture(true);
      onOpen();
    }
    prevAuto.current = p.defaultOpen;
  }, [p.defaultOpen, p.openProp, onOpen]);
  const handleOpenChange = (next: boolean, details: { reason?: string } | undefined) => {
    if (next && p.disabled) return;
    /* Clique no campo com o popover aberto é posicionamento de cursor no
       input, não pedido de fechar. */
    if (!next && closedByTriggerPress(details)) return;
    setOpenLocal(next);
    setOpenedByGesture(next);
    p.onOpenChange?.(next);
    // Controlado, a abertura é a do host: o effect abaixo a vê chegar.
    if (next && p.openProp === undefined) p.onOpen();
  };
  const close = () => {
    setOpenLocal(false);
    setOpenedByGesture(false);
    p.onOpenChange?.(false);
  };
  /* Fechou por fora (o host controlado baixou `open`): a próxima abertura
     decide de novo se veio de gesto. On the closing edge, during render. */
  const [seenOpen, setSeenOpen] = useState(open);
  if (seenOpen !== open) {
    setSeenOpen(open);
    if (!open) setOpenedByGesture(false);
  }
  /* Sob abertura CONTROLADA toda abertura é a do host — o gesto no gatilho, o
     guia que abre o campo ao nascer ou pelo Buscar (#5085) — e todas zeram o
     termo para o rascunho: aberto sem passar pelo gatilho, o campo nascia
     vazio e perdia o termo que o agente ouviu. */
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current && p.openProp !== undefined) onOpen();
    wasOpen.current = open;
  }, [open, p.openProp, onOpen]);
  return { open, close, handleOpenChange, focusOnOpen: open && (openedByGesture || p.hostGesture) };
}

export type PlaceFieldCopy = PlacePanelCopy & {
  /** Abaixo de `minChars` sem sugestão pré-digitação, o painel pede o termo.
   *  Só faz sentido na busca AUTÔNOMA (`onSearch`) — a controlada mostra o que
   *  o host mandar desde a primeira tecla. */
  promptLabel?: string;
};

/** Busca CONTROLADA PELO HOST — o modelo do `Onde` do hotel: o dono do campo
 *  guarda o ciclo (estado + sugestões) e recebe cada tecla por `onQueryChange`;
 *  o campo só espelha. Presente, o par `onSearch`/`zeroQuery` é ignorado. */
export type PlaceFieldControlledSearch<S extends PlaceSuggestion> = {
  readonly state: PlaceSearchState;
  readonly suggestions: readonly S[];
  readonly onQueryChange: (query: string) => void;
};

/**
 * O teto da lista (os 15.25rem do `PLACE_LIST_CLASS`, ver a conta lá) capado
 * pela `--available-height` do Base UI — em janela baixa quem manda é a tela.
 * O desconto de 1rem é a folga pro único chrome acima da lista, o `p-1` do
 * `Command`; descontar mais parava a dropdown antes do chão que o
 * `collisionPadding` já garante. A barra de rolagem é a compartilhada com o
 * `PlacePanel`.
 */
const LIST_CLASS =
  'max-h-[min(15.25rem,calc(var(--available-height)-1rem))] ' + PLACE_LIST_SCROLLBAR_CLASS;

/** O ciclo da busca remota do campo — `useRemoteSearch` (compartilhado) com o
 *  termo EFETIVO deste campo: só busca aberto, com o mínimo de caracteres e
 *  nunca vazio (o zero-query do campo são as sugestões do host, não uma busca).
 *  `canSearch`/`query` mantêm o vocabulário que o resto do campo já lê. */
function usePlaceSearch<S extends PlaceSuggestion>(p: {
  open: boolean;
  term: string;
  minChars: number;
  debounceMs: number;
  onSearch: (term: string) => Promise<readonly S[]>;
}) {
  const live = p.term.trim();
  const enabled = p.open && live !== '' && live.length >= p.minChars;
  const remote = useRemoteSearch({
    enabled,
    term: live,
    debounceMs: p.debounceMs,
    search: p.onSearch,
  });
  const query = remote.query ?? '';
  return {
    query,
    canSearch: query !== '',
    state: remote.state,
    results: remote.results,
    retry: remote.retry,
  };
}

/** Nada a buscar: o degrade estável da busca autônoma quando o host passou a
 *  CONTROLADA (`search`) — o hook roda sempre (regra de hooks), mas não sai
 *  request nenhum porque `term` chega vazio. */
const NO_SEARCH = () => Promise.resolve<readonly never[]>([]);

/** O que o painel MOSTRA, derivado da fonte da busca. Controlada: espelha o
 *  host desde a primeira tecla (sem prompt). Autônoma: prompt abaixo de
 *  `minChars` sem sugestão pré-digitação (o trim é do termo VIVO — a dica não
 *  espera o debounce), zero-query fora da busca, e o ciclo do hook no resto. */
function panelView<S extends PlaceSuggestion>(p: {
  search: PlaceFieldControlledSearch<S> | undefined;
  term: string;
  minChars: number;
  promptLabel: string | undefined;
  zeroQuery: readonly S[] | undefined;
  auto: { query: string; canSearch: boolean; state: PlaceSearchState; results: readonly S[] };
}): { query: string; shown: readonly S[]; state: PlaceSearchState; showPrompt: boolean } {
  if (p.search) {
    return {
      query: p.term.trim(),
      shown: p.search.suggestions,
      state: p.search.state,
      showPrompt: false,
    };
  }
  return {
    query: p.auto.query,
    shown: p.auto.canSearch ? p.auto.results : (p.zeroQuery ?? []),
    state: p.auto.canSearch ? p.auto.state : 'ready',
    showPrompt:
      p.promptLabel !== undefined &&
      p.term.trim().length < p.minChars &&
      (p.zeroQuery?.length ?? 0) === 0,
  };
}

/** O rascunho (`draft`) só vale com o campo vazio: o termo que abre a busca e o
 *  texto do campo fechado. */
function draftView(
  value: PlaceFieldValue | undefined,
  draft: string | undefined,
  emptyText: string,
): { seed: string; shownEmpty: string } {
  const seed = value || !draft ? '' : draft;
  return { seed, shownEmpty: seed || emptyText };
}

export function PlaceField<S extends PlaceSuggestion>({
  label,
  value,
  emptyText,
  icon,
  variant = 'outline',
  disabled = false,
  copy,
  minChars = 2,
  debounceMs = 300,
  zeroQuery,
  onSearch,
  search,
  header,
  selectedPlaceId,
  onPick,
  open: openProp,
  onOpenChange,
  defaultOpen = false,
  sheet,
  wanted = false,
  openedByGesture = false,
  draft,
}: {
  /** Nome acessível do campo (`Origem`, `Destino`) — o rótulo visível, quando
   *  existe, é de quem compõe a coluna. */
  readonly label: string;
  readonly value?: PlaceFieldValue;
  /** O texto do vazio, que nomeia O QUE SE ESCOLHE (`Cidade ou aeroporto`). */
  readonly emptyText: string;
  /** O glifo fixo do campo (decolagem/pouso no aéreo). Decorativo — o wrapper
   *  já o esconde de leitor de tela. */
  readonly icon?: ReactNode;
  /** Ver `PlaceFieldVariant`. Ausente ⇒ `outline`. */
  readonly variant?: PlaceFieldVariant;
  readonly disabled?: boolean;
  readonly copy: PlaceFieldCopy;
  readonly minChars?: number;
  readonly debounceMs?: number;
  /** Sugestões PRÉ-DIGITAÇÃO (ex.: "Recentes", via `groupLabel`). Ausente ⇒ o
   *  campo aberto pede o termo (`promptLabel`). */
  readonly zeroQuery?: readonly S[];
  /** Busca AUTÔNOMA: o campo mesmo debounça e dispara. PRECISA ser
   *  referencialmente estável (memoizada pelo host) — é dep do effect da
   *  busca; uma arrow inline re-dispararia a busca a cada render. Ausente,
   *  quem alimenta o painel é a `search` controlada. */
  readonly onSearch?: (term: string) => Promise<readonly S[]>;
  /** Busca CONTROLADA pelo host — ver `PlaceFieldControlledSearch`. Vence o
   *  `onSearch` quando os dois vierem. */
  readonly search?: PlaceFieldControlledSearch<S>;
  /** Fatia acima dos resultados na exibição ZERO-QUERY (a linha "perto de mim"
   *  do hotel) — conteúdo de `CommandList` (grupos/itens do cmdk). Some
   *  enquanto se digita: com termo, o cmdk re-seleciona o item #0 a cada
   *  mudança de busca e o realce ficaria preso nela (a razão documentada no
   *  `MyLocationRow` do hotel). */
  readonly header?: ReactNode;
  /** O lugar já escolhido, pro `aria-current` da linha correspondente. */
  readonly selectedPlaceId?: string;
  readonly onPick: (place: S) => void;
  /** Abertura controlada de fora — quem passa um passa os dois. Ausente, o
   *  campo se governa. */
  readonly open?: boolean;
  readonly onOpenChange?: (open: boolean) => void;
  /** Abre por CONTA DO HOST — o auto-avanço: verdadeiro no mount abre já no
   *  mount; a TRANSIÇÃO falso→verdadeiro depois dele também abre (o foco da
   *  vez chega por prop quando o campo anterior fecha). Nunca fecha nada, e a
   *  pessoa segue podendo fechar (Esc/fora) sem o campo reabrir sozinho.
   *  Ignorado sob abertura controlada. */
  readonly defaultOpen?: boolean;
  /** FOLHA de tela cheia em vez do popover (#2653, ver `PlaceSheet`); a busca
   *  acontece nela, não no campo. Ausente ⇒ popover. `subtitle` é a linha de
   *  contexto sob o título (a origem já escolhida, na folha do Destino). */
  readonly sheet?: { readonly backLabel: string; readonly subtitle?: string };
  /** O campo PEDE A VEZ — o anel de pendente do preenchimento guiado
   *  (`data-wanted` na pílula, ver `FIELD_TRIGGER_OPEN_RING_CLASS`). */
  readonly wanted?: boolean;
  /** Sob abertura CONTROLADA: esta abertura é a CONTINUAÇÃO de um gesto da
   *  pessoa (o preenchimento guiado abrindo o campo seguinte), então o input
   *  nasce focado — como no clique. Ausente, um `open` que vira verdadeiro por
   *  conta do host deixa o cursor onde estava (a barra do hotel abrindo um
   *  painel não pode roubá-lo de quem digita no composer). */
  readonly openedByGesture?: boolean;
  /** Um termo que a pessoa já disse e ninguém resolveu ainda ("Brusque"): sem
   *  `value`, o campo fechado o mostra no lugar do `emptyText` e abre já buscando
   *  por ele. Escolhido um lugar, o rascunho deixa de valer. */
  readonly draft?: string;
}): ReactNode {
  const { seed, shownEmpty } = draftView(value, draft, emptyText);
  const [term, setTerm] = useState('');
  const onTerm = (next: string) => {
    setTerm(next);
    search?.onQueryChange(next);
  };
  const { open, close, handleOpenChange, focusOnOpen } = usePlaceFieldOpen({
    openProp,
    onOpenChange,
    defaultOpen,
    hostGesture: openedByGesture,
    disabled,
    onOpen: () => {
      setTerm(seed);
      // O host controlado precisa saber que o rascunho zerou (senão a lista reabre filtrada).
      search?.onQueryChange(seed);
    },
  });
  // O `[cmdk-root]` do painel: pra lá vão as setas e o Enter do input.
  const panelRef = useRef<HTMLDivElement>(null);
  // Sob busca controlada o hook autônomo roda vazio (regra de hooks): o ciclo é do host.
  const auto = usePlaceSearch({
    open,
    term: search ? '' : term,
    minChars,
    debounceMs,
    onSearch: onSearch ?? NO_SEARCH,
  });
  const view = panelView({
    search,
    term,
    minChars,
    promptLabel: copy.promptLabel,
    zeroQuery,
    auto,
  });
  const pick = (place: S) => {
    // Fechar é só de quem NÃO tem dono da abertura: sob abertura controlada, o
    // pai decide o que vem depois de escolher (um `close` aqui desfaria o campo que ele abriu).
    if (openProp === undefined) close();
    onPick(place);
  };
  const list = (
    <PlaceListPanel
      view={view}
      copy={copy}
      label={label}
      header={header}
      selectedPlaceId={selectedPlaceId}
      sheet={sheet !== undefined}
      onPick={pick}
      {...(search ? {} : { onRetry: auto.retry })}
    />
  );
  if (sheet) {
    return (
      <PlaceSheet
        open={open}
        disabled={disabled}
        label={label}
        backLabel={sheet.backLabel}
        {...(sheet.subtitle ? { subtitle: sheet.subtitle } : {})}
        variant={variant}
        value={value}
        emptyText={shownEmpty}
        icon={icon}
        term={term}
        onTerm={onTerm}
        panelRef={panelRef}
        wanted={wanted}
        onOpen={() => {
          handleOpenChange(true, undefined);
        }}
        onClose={close}
      >
        {list}
      </PlaceSheet>
    );
  }

  return (
    <PlacePopover
      open={open}
      onOpenChange={handleOpenChange}
      disabled={disabled}
      label={label}
      panelLabel={copy.label}
      variant={variant}
      wanted={wanted}
      value={value}
      emptyText={shownEmpty}
      icon={icon}
      term={term}
      onTerm={onTerm}
      panelRef={panelRef}
      onEscape={close}
      focusOnOpen={focusOnOpen}
    >
      {list}
    </PlacePopover>
  );
}

/** O POPOVER do campo (a casca larga): a pílula é o gatilho do Base UI e, aberta,
 *  hospeda o input; a lista sai por portal. Fora de `PlaceField` pelo teto de
 *  linhas dele. */
function PlacePopover({
  open,
  onOpenChange,
  disabled,
  label,
  panelLabel,
  variant,
  wanted,
  value,
  emptyText,
  icon,
  term,
  onTerm,
  panelRef,
  onEscape,
  focusOnOpen,
  children,
}: {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean, details: { reason?: string } | undefined) => void;
  readonly disabled: boolean;
  readonly label: string;
  /** O nome acessível do popup (`copy.label`). */
  readonly panelLabel: string;
  readonly variant: PlaceFieldVariant;
  readonly wanted: boolean;
  readonly value: PlaceFieldValue | undefined;
  readonly emptyText: string;
  readonly icon: ReactNode | undefined;
  readonly term: string;
  readonly onTerm: (term: string) => void;
  readonly panelRef: RefObject<HTMLDivElement | null>;
  readonly onEscape: () => void;
  readonly focusOnOpen: boolean;
  readonly children: ReactNode;
}): ReactNode {
  /* O gatilho é a referência do Base UI, e é a ele que o foco volta ao fechar
     — salvo quando já pousou noutro lugar: no preenchimento guiado o campo
     seguinte abre (e foca o input dele) enquanto este ainda desmonta, e um
     `finalFocus` em FUNÇÃO conta como explícito pro Base UI, que então devolve
     o foco sempre — derrubando o popover recém-aberto por `focus-out`. */
  const triggerRef = useRef<HTMLDivElement>(null);
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      {/* `<div>` e não `<button>`: aberto, o gatilho hospeda o `<input>` da
          busca, e input dentro de button é HTML inválido. O `role="button"` só
          vale FECHADO — button achata os filhos no nome acessível, então com o
          input dentro o papel sai e quem fala com o leitor de tela é o input.
          `pointer-events-none` + tabIndex reencenam o `disabled` nativo; o
          bloqueio de verdade está no `onOpenChange`. */}
      <PopoverTrigger
        nativeButton={false}
        render={
          <TriggerShell
            ref={triggerRef}
            open={open}
            disabled={disabled}
            label={label}
            variant={variant}
            {...wantedAttr(wanted)}
          />
        }
      >
        <TriggerContent
          open={open}
          {...(value ? { value } : {})}
          emptyText={emptyText}
          icon={icon}
          label={label}
          term={term}
          onTerm={onTerm}
          panelRef={panelRef}
          onEscape={onEscape}
          focusOnOpen={focusOnOpen}
        />
      </PopoverTrigger>
      {/* `genui2` no wrapper do portal: o popover renderiza no `<body>`, fora da
          raiz do tema, e sem a classe herda os tokens do CSS legado do host.
          `initialFocus={false}`: o foco fica no input DO CAMPO — deixar o popup
          puxá-lo pra dentro desfaria a busca no campo no instante em que abre. */}
      {/* `aria-label`: o popup do Base UI é `role="dialog"`, e sem nome ele é
          só "diálogo" pro leitor de tela — e invisível pra quem procura o
          painel pelo assunto do campo (`Destino`, `Origem`). */}
      <PopoverContent
        className="genui2 w-80 rounded-xl p-0"
        align="start"
        initialFocus={false}
        /* Fechou por um PONTEIRO fora do popover: o foco fica onde a pessoa
           clicou — devolvê-lo ao gatilho roubaria o clique dela. Nos demais
           fechamentos (Escape, pick) o gatilho recebe o foco — a menos que ele
           já tenha ido pra outro lugar (ver `returnFocusUnlessMoved`). */
        finalFocus={(closeType) =>
          ['mouse', 'touch', 'pen'].includes(closeType)
            ? false
            : returnFocusUnlessMoved(triggerRef)()
        }
        aria-label={panelLabel}
      >
        <div ref={panelRef} className="contents">
          {children}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/** A lista do campo — a mesma no popover e na folha (o root e a regra do
 *  realce são do `PlaceCommand`). Na folha a lista toma a altura que houver. */
function PlaceListPanel<S extends PlaceSuggestion>({
  view,
  copy,
  label,
  header,
  selectedPlaceId,
  sheet,
  onPick,
  onRetry,
}: {
  readonly view: ReturnType<typeof panelView<S>>;
  readonly copy: PlaceFieldCopy;
  readonly label: string;
  readonly header: ReactNode;
  readonly selectedPlaceId: string | undefined;
  readonly sheet: boolean;
  readonly onPick: (place: S) => void;
  readonly onRetry?: () => void;
}): ReactNode {
  return (
    <PlaceCommand
      query={view.query}
      suggestions={view.shown}
      selectedPlaceId={selectedPlaceId}
      label={label}
      {...(sheet ? { className: 'bg-transparent p-0' } : {})}
    >
      <CommandList className={sheet ? `max-h-none ${PLACE_LIST_SCROLLBAR_CLASS}` : LIST_CLASS}>
        {/* O header some com termo digitado — ver a doc da prop. */}
        {view.query ? null : header}
        {view.showPrompt ? (
          <CommandEmpty>
            <span className="text-body">{copy.promptLabel}</span>
          </CommandEmpty>
        ) : (
          <PlaceResults
            copy={copy}
            state={view.state}
            suggestions={view.shown}
            {...(selectedPlaceId !== undefined ? { selectedPlaceId } : {})}
            query={view.query}
            onPick={onPick}
            {...(onRetry ? { onRetry } : {})}
          />
        )}
      </CommandList>
    </PlaceCommand>
  );
}
